import { isDominoTask } from './dailyTaskTypes';

/** Sales executive domino task types for add-lead + pricing flows. */
export const SALES_EXEC_ADD_LEAD_PRICING_UC = 'SALES_EXEC_ADD_LEAD_PRICING_UC';
export const SALES_EXEC_ADD_LEAD_PRICING_BOOKED = 'SALES_EXEC_ADD_LEAD_PRICING_BOOKED';

export const SALES_EXEC_ADD_LEAD_PRICING_TASK_TYPES = [
  SALES_EXEC_ADD_LEAD_PRICING_UC,
  SALES_EXEC_ADD_LEAD_PRICING_BOOKED
];

/** Sales manager domino task — opens Unit Inventory bulk update. */
export const SALES_BULK_UPDATE_UNIT_STATUS = 'Bulk Update Unit Status';

/** Sales manager domino task — opens Leads page with bulk upload modal. */
export const BULK_UPLOAD_EXISTING_LEAD = 'Bulk Upload Existing Lead';

/** Sales manager domino task — upload document on a closed deal for a unit. */
export const ADD_DOCUMENT_TO_CLOSED_DEAL = 'Add Document to Closed Deal';

export function normalizeSalesDailyTaskType(type) {
  return String(type || '').trim();
}

export function salesDailyTaskTypesMatch(left, right) {
  return (
    normalizeSalesDailyTaskType(left).toLowerCase() ===
    normalizeSalesDailyTaskType(right).toLowerCase()
  );
}

/** Normalize sales daily task API payloads into a flat array. */
export function normalizeSalesDailyTaskList(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
}

export function isSalesLeadPricingProgressionType(type) {
  const normalized = normalizeSalesDailyTaskType(type);
  return (
    normalized === SALES_EXEC_ADD_LEAD_PRICING_UC ||
    normalized === SALES_EXEC_ADD_LEAD_PRICING_BOOKED ||
    normalized === 'Add Lead and Pricing - Under Consideration' ||
    normalized === 'Add Lead and Pricing - Booked' ||
    normalized.startsWith('Add Lead and Pricing')
  );
}

export function isSalesExecAddLeadPricingUcTask(task) {
  const normalized = normalizeSalesDailyTaskType(task?.type);
  return (
    normalized === SALES_EXEC_ADD_LEAD_PRICING_UC ||
    normalized === 'Add Lead and Pricing - Under Consideration'
  );
}

export function isSalesExecAddLeadPricingBookedTask(task) {
  const normalized = normalizeSalesDailyTaskType(task?.type);
  return (
    normalized === SALES_EXEC_ADD_LEAD_PRICING_BOOKED ||
    normalized === 'Add Lead and Pricing - Booked'
  );
}

export function isDominoLeadPricingTask(task) {
  return isSalesExecAddLeadPricingUcTask(task) || isSalesExecAddLeadPricingBookedTask(task);
}

export function isBulkUpdateUnitStatusDominoTask(task) {
  return normalizeSalesDailyTaskType(task?.type) === SALES_BULK_UPDATE_UNIT_STATUS;
}

export function isBulkUploadExistingLeadDominoTask(task) {
  return normalizeSalesDailyTaskType(task?.type) === BULK_UPLOAD_EXISTING_LEAD;
}

export function isAddDocumentToClosedDealDominoTask(task) {
  return salesDailyTaskTypesMatch(task?.type, ADD_DOCUMENT_TO_CLOSED_DEAL);
}

/** Pending tasks shown in the Sales dashboard Domino tab. */
export function isPendingSalesDominoTask(task) {
  return String(task?.status || '').toLowerCase() !== 'completed' && isDominoTask(task);
}

export function getDominoTaskFlowType(task) {
  if (isSalesExecAddLeadPricingBookedTask(task)) return 'dealClosed';
  if (isSalesExecAddLeadPricingUcTask(task)) return 'underConsideration';
  return null;
}

/** Context label shown on domino task rows in the action center. */
export function getSalesDominoUnitContextLabel(task) {
  if (isSalesExecAddLeadPricingUcTask(task)) {
    return 'Created under consideration';
  }
  if (isSalesExecAddLeadPricingBookedTask(task)) {
    return 'Created deal closed';
  }
  if (isAddDocumentToClosedDealDominoTask(task) && task?.unit?.unitName) {
    return task.unit.unitName;
  }
  if (isBulkUploadExistingLeadDominoTask(task) && task?.project?.name) {
    return task.project.name;
  }
  return null;
}

export function getDominoTaskUcDetails(task) {
  const unit = task?.unit;
  if (!unit) return null;

  return {
    project: unit.project_id ?? task?.project_id ?? null,
    block: unit.block_id ?? null,
    level: unit.level_id ?? null,
    unit: unit.id ?? task?.unit_id ?? null
  };
}

export function getSalesDominoModalKind(item) {
  const type = item?.type;
  if (isDominoLeadPricingTask({ type }) || isSalesLeadPricingProgressionType(type)) {
    return 'leadPricing';
  }
  if (isAddDocumentToClosedDealDominoTask({ type })) {
    return 'dealDocument';
  }
  return null;
}

export function getSalesProgressionSubStepActionLabel(subStep) {
  if (subStep?.actionLabel) return subStep.actionLabel;
  const modalKind = getSalesDominoModalKind(subStep);
  if (modalKind === 'leadPricing') return 'Add lead';
  if (modalKind === 'dealDocument') return 'Add document';
  return null;
}

/** Resolve a domino task record for progression modals. */
export function resolveSalesDominoTaskFromList(tasks, { taskId, type, unit, projectId }) {
  const list = normalizeSalesDailyTaskList(tasks);

  if (taskId != null) {
    const match = list.find((task) => Number(task.id) === Number(taskId));
    if (match) return match;
  }

  if (type) {
    const unitId = unit?.id ?? unit ?? null;
    const pendingMatch = list.find((task) => {
      if (!salesDailyTaskTypesMatch(task.type, type)) return false;
      if (String(task?.status || '').toLowerCase() === 'completed') return false;
      if (unitId == null) return true;
      const taskUnitId = task.unit?.id ?? task.unit_id ?? null;
      return Number(taskUnitId) === Number(unitId);
    });
    if (pendingMatch) return pendingMatch;
  }

  if (type && (unit || taskId != null)) {
    const resolvedUnit = unit && typeof unit === 'object' ? unit : null;
    return {
      id: taskId ?? null,
      type,
      unit: resolvedUnit,
      unit_id: resolvedUnit?.id ?? (unit != null && typeof unit !== 'object' ? unit : null),
      project_id: projectId ?? resolvedUnit?.project_id ?? null,
      maintype: 'domino'
    };
  }

  return null;
}

/** Navigation targets for sales onboarding progression step actions. */
export function getSalesPriorityNavigation(taskType) {
  const normalizedType = normalizeSalesDailyTaskType(taskType);

  if (normalizedType === SALES_BULK_UPDATE_UNIT_STATUS) {
    return {
      path: '/builder/sales/',
      secondaryTab: 'Unit Inventory',
      openBulkUnitUpdate: true
    };
  }

  if (normalizedType === BULK_UPLOAD_EXISTING_LEAD) {
    return { path: '/builder/sales/', secondaryTab: 'Leads' };
  }

  if (salesDailyTaskTypesMatch(normalizedType, ADD_DOCUMENT_TO_CLOSED_DEAL)) {
    return { path: '/builder/sales/closed-won' };
  }

  if (
    normalizedType === 'Add Lead and Pricing - Booked' ||
    normalizedType === 'Add Lead and Pricing - Under Consideration' ||
    normalizedType.startsWith('Add Lead and Pricing')
  ) {
    return { path: '/builder/sales/' };
  }

  return null;
}
